import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, collection, query, getDocs, writeBatch } from 'firebase/firestore';
import { getAuth, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';

// --- Componente Modal para Vaciar Base de Datos (Sin cambios) ---
const ClearDatabaseModal = ({ user, onClose, setNotification }) => {
    const [password, setPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selections, setSelections] = useState({
        clients: true, visits: true, quotes: true, expenses: true, accounting: true,
        counters: true, quoteCounter: true, expenseCounter: true, accountingCounter: true,
    });

    const collectionsToClear = {
        clients: "Clientes", visits: "Informes de Visita", quotes: "Cotizaciones",
        expenses: "Egresos", accounting: "Registros Contables",
    };

    const countersToReset = {
        counters: { path: 'counters/visitCounter', label: "Folios de Informes" },
        quoteCounter: { path: 'quoteCounter/counter', label: "Folios de Cotizaciones" },
        expenseCounter: { path: 'expenseCounter/counter', label: "Folios de Egresos" },
        accountingCounter: { path: 'accountingCounter/counter', label: "Folios Contables Manuales" },
    };

    const handleCheckboxChange = (key) => setSelections(prev => ({ ...prev, [key]: !prev[key] }));

    const handleClearDatabase = async () => {
        if (!password) { setNotification({ message: 'Ingrese contraseña.', type: 'error' }); return; }
        setIsSubmitting(true);
        try {
            const auth = getAuth();
            const credential = EmailAuthProvider.credential(user.email, password);
            await reauthenticateWithCredential(auth.currentUser, credential);
            const batch = writeBatch(db);

            for (const key in collectionsToClear) {
                if (selections[key]) {
                    const snapshot = await getDocs(query(collection(db, key)));
                    snapshot.docs.forEach(doc => batch.delete(doc.ref));
                }
            }
            for (const key in countersToReset) {
                if (selections[key]) {
                    batch.set(doc(db, countersToReset[key].path), { current: 0 }, { merge: true });
                }
            }
            await batch.commit();
            setNotification({ message: 'Base de datos limpiada.', type: 'success' });
            onClose();
        } catch (error) {
            console.error(error);
            setNotification({ message: 'Error: Contraseña incorrecta o falta de permisos.', type: 'error' });
        } finally { setIsSubmitting(false); }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-8 w-full max-w-2xl shadow-xl border border-transparent dark:border-gray-700">
                <h2 className="text-2xl font-bold mb-4 text-red-600 dark:text-red-400">Confirmar Vaciado</h2>
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contraseña:</label>
                    <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" required />
                </div>
                <div className="flex justify-end gap-4">
                    <button onClick={onClose} className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-4 py-2 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">Cancelar</button>
                    <button onClick={handleClearDatabase} disabled={isSubmitting} className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50 transition-colors">Confirmar</button>
                </div>
            </div>
        </div>
    );
};

const ParametersPage = ({ setPage, setNotification }) => {
    const { user } = useAuth();
    
    // --- Estados Generales ---
    const [iva, setIva] = useState(19);
    const [gastosFijos, setGastosFijos] = useState(10);
    const [ppm, setPpm] = useState(3);
    const [utilidad, setUtilidad] = useState(20);
    
    // Listas Configurables
    const [unidades, setUnidades] = useState(['mt', 'unid', 'gl', 'm2']);
    const [newUnit, setNewUnit] = useState('');
    
    const [paymentMethods, setPaymentMethods] = useState(['Contado', 'Contraentrega', '30 Días']);
    const [newPaymentMethod, setNewPaymentMethod] = useState('');
    
    const [visitTypes, setVisitTypes] = useState(['Mantención', 'Reparación', 'Instalación', 'Visita Técnica']);
    const [newVisitType, setNewVisitType] = useState('');

    // --- NUEVO: Estados Finales ---
    const [finalStatuses, setFinalStatuses] = useState(['100% Operativo', 'Operativo con Observaciones', 'Pendiente de Repuestos', 'Fuera de Servicio']);
    const [newFinalStatus, setNewFinalStatus] = useState('');

    // Textos y Bancos
    const [headerInfo, setHeaderInfo] = useState('');
    const [footerInfo, setFooterInfo] = useState('');
    const [bankBeneficiaryName, setBankBeneficiaryName] = useState('');
    const [bankRut, setBankRut] = useState('');
    const [bankName, setBankName] = useState('');
    const [bankAccount, setBankAccount] = useState('');
    const [bankAccountType, setBankAccountType] = useState('Cuenta Corriente');
    const [bankEmail, setBankEmail] = useState('');

    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showClearModal, setShowClearModal] = useState(false);

    // Colores PDF
    const [pdfColors, setPdfColors] = useState({
        portada: '#1E3A8A',
        sectionTitle: '#1E3A8A',
        signature: '#374151',
        text: '#374151',
        footer: '#6B7280'
    });
    const updatePdfColor = (key, value) => setPdfColors(prev => ({ ...prev, [key]: value }));

    // --- Cargar Parámetros ---
    useEffect(() => {
        const fetchParameters = async () => {
            try {
                const docSnap = await getDoc(doc(db, 'parameters', 'system-settings'));
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setIva(data.iva || 19);
                    setGastosFijos(data.gastosFijos || 10);
                    setPpm(data.ppm || 3);
                    setUtilidad(data.utilidad || 20);
                    
                    if (data.unidades) setUnidades(data.unidades);
                    if (data.paymentMethods) setPaymentMethods(data.paymentMethods);
                    if (data.visitTypes) setVisitTypes(data.visitTypes);
                    // Cargar estados finales si existen, sino usar default
                    if (data.finalStatuses) setFinalStatuses(data.finalStatuses);
                    
                    setHeaderInfo(data.headerInfo || '');
                    setFooterInfo(data.footerInfo || '');
                    setBankBeneficiaryName(data.bankBeneficiaryName || '');
                    setBankRut(data.bankRut || '');
                    setBankName(data.bankName || '');
                    setBankAccount(data.bankAccount || '');
                    setBankAccountType(data.bankAccountType || 'Cuenta Corriente');
                    setBankEmail(data.bankEmail || '');
                    if (data.pdfColors) setPdfColors(prev => ({ ...prev, ...data.pdfColors }));
                }
            } catch (error) {
                setNotification({ message: 'Error al cargar parámetros.', type: 'error' });
            } finally { setLoading(false); }
        };
        fetchParameters();
    }, [setNotification]);

    // --- Handlers Genéricos para Listas ---
    const addItem = (list, setList, newItem, setNewItem) => {
        if (newItem && !list.includes(newItem.trim())) {
            setList([...list, newItem.trim()]);
            setNewItem('');
        }
    };
    const deleteItem = (list, setList, itemToDelete) => {
        setList(list.filter(item => item !== itemToDelete));
    };

    // --- Guardar Todo ---
    const handleSave = async () => {
        setIsSubmitting(true);
        try {
            await setDoc(doc(db, 'parameters', 'system-settings'), {
                iva, gastosFijos, ppm, utilidad, 
                unidades, paymentMethods, visitTypes, 
                finalStatuses,
                headerInfo, footerInfo,
                bankBeneficiaryName, bankRut, bankName, bankAccount, bankAccountType, bankEmail,
                pdfColors
            }, { merge: true });
            setNotification({ message: 'Parámetros guardados.', type: 'success' });
        } catch (error) {
            setNotification({ message: 'Error al guardar.', type: 'error' });
        } finally { setIsSubmitting(false); }
    };

    if (loading) return <div className="p-8 text-center text-gray-500 dark:text-gray-400">Cargando...</div>;

    return (
        <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300">
             {showClearModal && <ClearDatabaseModal user={user} onClose={() => setShowClearModal(false)} setNotification={setNotification} />}
            <div className="flex items-center mb-6">
                <button onClick={() => setPage('hub')} className="text-blue-600 dark:text-blue-400 hover:underline font-semibold mr-4">&larr; Volver</button>
                <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">Parámetros del Sistema</h1>
            </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Columna Izquierda */}
                <div className="space-y-6">
                    {/* Porcentajes */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-transparent dark:border-gray-700 transition-colors">
                        <h2 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-200 border-b dark:border-gray-700 pb-2">Valores Financieros (%)</h2>
                        <div className="grid grid-cols-2 gap-4">
                            <div><label className="text-sm font-medium text-gray-700 dark:text-gray-300">IVA</label><input type="number" value={iva} onChange={e => setIva(parseFloat(e.target.value))} className="w-full border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"/></div>
                            <div><label className="text-sm font-medium text-gray-700 dark:text-gray-300">Gastos Fijos</label><input type="number" value={gastosFijos} onChange={e => setGastosFijos(parseFloat(e.target.value))} className="w-full border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"/></div>
                            <div><label className="text-sm font-medium text-gray-700 dark:text-gray-300">PPM</label><input type="number" value={ppm} onChange={e => setPpm(parseFloat(e.target.value))} className="w-full border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"/></div>
                            <div><label className="text-sm font-medium text-gray-700 dark:text-gray-300">Utilidad Base</label><input type="number" value={utilidad} onChange={e => setUtilidad(parseFloat(e.target.value))} className="w-full border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"/></div>
                        </div>
                    </div>

                    {/* Tipos de Visita */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-transparent dark:border-gray-700 transition-colors">
                        <h2 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-200 border-b dark:border-gray-700 pb-2">Tipos de Visita</h2>
                        <div className="flex gap-2 mb-2">
                            <input type="text" value={newVisitType} onChange={e => setNewVisitType(e.target.value)} placeholder="Ej: Emergencia" className="flex-grow border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"/>
                            <button onClick={() => addItem(visitTypes, setVisitTypes, newVisitType, setNewVisitType)} className="bg-blue-500 text-white px-4 rounded hover:bg-blue-600 transition-colors">+</button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {visitTypes.map(t => <span key={t} className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2 py-1 rounded flex items-center gap-2">{t} <button onClick={() => deleteItem(visitTypes, setVisitTypes, t)} className="text-red-500 dark:text-red-400 font-bold hover:text-red-700 dark:hover:text-red-300 transition-colors">×</button></span>)}
                        </div>
                    </div>

                    {/* --- NUEVO: Estados Finales --- */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border-l-4 border-green-500 dark:border-green-600 transition-colors">
                        <h2 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-200 border-b dark:border-gray-700 pb-2">Estados Finales de Informe</h2>
                        <div className="flex gap-2 mb-2">
                            <input type="text" value={newFinalStatus} onChange={e => setNewFinalStatus(e.target.value)} placeholder="Ej: Requiere Segunda Visita" className="flex-grow border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"/>
                            <button onClick={() => addItem(finalStatuses, setFinalStatuses, newFinalStatus, setNewFinalStatus)} className="bg-green-500 text-white px-4 rounded hover:bg-green-600 transition-colors">+</button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {finalStatuses.map(s => (
                                <span key={s} className="bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800/50 px-2 py-1 rounded flex items-center gap-2 text-sm">
                                    {s} <button onClick={() => deleteItem(finalStatuses, setFinalStatuses, s)} className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-bold transition-colors">×</button>
                                </span>
                            ))}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Estos estados aparecerán como menú desplegable en los informes técnicos.</p>
                    </div>
                </div>

                {/* Columna Derecha */}
                <div className="space-y-6">
                    {/* Unidades y Pagos */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-transparent dark:border-gray-700 transition-colors">
                        <h2 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-200 border-b dark:border-gray-700 pb-2">Listas Generales</h2>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Unidades de Medida</label>
                            <div className="flex gap-2 mb-2">
                                <input type="text" value={newUnit} onChange={e => setNewUnit(e.target.value)} placeholder="Ej: Kit" className="flex-grow border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"/>
                                <button onClick={() => addItem(unidades, setUnidades, newUnit, setNewUnit)} className="bg-blue-500 text-white px-4 rounded hover:bg-blue-600 transition-colors">+</button>
                            </div>
                            <div className="flex flex-wrap gap-2">{unidades.map(u => <span key={u} className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2 py-1 rounded text-sm flex items-center gap-1">{u} <button onClick={() => deleteItem(unidades, setUnidades, u)} className="text-red-500 dark:text-red-400 font-bold hover:text-red-700 dark:hover:text-red-300 transition-colors ml-1">×</button></span>)}</div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Formas de Pago</label>
                            <div className="flex gap-2 mb-2">
                                <input type="text" value={newPaymentMethod} onChange={e => setNewPaymentMethod(e.target.value)} placeholder="Ej: 60 Días" className="flex-grow border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"/>
                                <button onClick={() => addItem(paymentMethods, setPaymentMethods, newPaymentMethod, setNewPaymentMethod)} className="bg-blue-500 text-white px-4 rounded hover:bg-blue-600 transition-colors">+</button>
                            </div>
                            <div className="flex flex-wrap gap-2">{paymentMethods.map(p => <span key={p} className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2 py-1 rounded text-sm flex items-center gap-1">{p} <button onClick={() => deleteItem(paymentMethods, setPaymentMethods, p)} className="text-red-500 dark:text-red-400 font-bold hover:text-red-700 dark:hover:text-red-300 transition-colors ml-1">×</button></span>)}</div>
                        </div>
                    </div>

                    {/* Personalización Docs */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-transparent dark:border-gray-700 transition-colors">
                        <h2 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-200 border-b dark:border-gray-700 pb-2">Textos en Documentos</h2>
                        <div className="space-y-3">
                            <div><label className="text-sm font-medium text-gray-700 dark:text-gray-300">Encabezado (Dirección/Tel)</label><textarea value={headerInfo} onChange={e => setHeaderInfo(e.target.value)} rows="3" className="w-full border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"/></div>
                            <div><label className="text-sm font-medium text-gray-700 dark:text-gray-300">Pie de Página (Legal/Web)</label><textarea value={footerInfo} onChange={e => setFooterInfo(e.target.value)} rows="3" className="w-full border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"/></div>
                        </div>
                    </div>

                    {/* Datos Bancarios */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-transparent dark:border-gray-700 transition-colors">
                        <h2 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-200 border-b dark:border-gray-700 pb-2">Datos Bancarios</h2>
                        <div className="space-y-2 text-sm">
                            <input type="text" placeholder="Nombre Beneficiario" value={bankBeneficiaryName} onChange={e => setBankBeneficiaryName(e.target.value)} className="w-full border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"/>
                            <input type="text" placeholder="RUT Beneficiario" value={bankRut} onChange={e => setBankRut(e.target.value)} className="w-full border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"/>
                            <input type="text" placeholder="Banco" value={bankName} onChange={e => setBankName(e.target.value)} className="w-full border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"/>
                            <div className="grid grid-cols-2 gap-2">
                                <input type="text" placeholder="N° Cuenta" value={bankAccount} onChange={e => setBankAccount(e.target.value)} className="w-full border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"/>
                                <select value={bankAccountType} onChange={e => setBankAccountType(e.target.value)} className="w-full border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"><option>Cuenta Corriente</option><option>Cuenta Vista</option><option>Cuenta Ahorro</option></select>
                            </div>
                            <input type="email" placeholder="Email Confirmación" value={bankEmail} onChange={e => setBankEmail(e.target.value)} className="w-full border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"/>
                        </div>
                    </div>

                    {/* Colores PDF */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border-l-4 border-blue-500 dark:border-blue-600 transition-colors">
                        <h2 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-200 border-b dark:border-gray-700 pb-2">🎨 Colores del PDF</h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Personaliza los colores que se usan al generar los informes técnicos en PDF.</p>
                        <div className="space-y-3">
                            {[
                                { key: 'portada', label: 'Portada y Encabezados', desc: 'Color del título en la portada y encabezados internos' },
                                { key: 'sectionTitle', label: 'Títulos de Sección', desc: 'Color de los títulos numerados (ej: 2. DESCRIPCIÓN GENERAL)' },
                                { key: 'signature', label: 'Línea de Firma', desc: 'Color de la línea y nombre del profesional' },
                                { key: 'text', label: 'Texto Principal', desc: 'Color del texto de contenido del informe' },
                                { key: 'footer', label: 'Pie de Página', desc: 'Color del texto del pie de página' }
                            ].map(item => (
                                <div key={item.key} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
                                    <input
                                        type="color"
                                        value={pdfColors[item.key]}
                                        onChange={e => updatePdfColor(item.key, e.target.value)}
                                        className="w-10 h-10 rounded cursor-pointer border-2 border-gray-300 dark:border-gray-600"
                                    />
                                    <div className="flex-1">
                                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">{item.label}</p>
                                        <p className="text-[10px] text-gray-400 dark:text-gray-500">{item.desc}</p>
                                    </div>
                                    <span className="font-mono text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{pdfColors[item.key]}</span>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 p-3 rounded-lg border dark:border-gray-600" style={{ backgroundColor: pdfColors.portada + '10' }}>
                            <p className="text-xs font-bold mb-1" style={{ color: pdfColors.portada }}>Vista previa</p>
                            <p className="text-[11px] font-bold mb-1" style={{ color: pdfColors.sectionTitle }}>2. TÍTULO DE SECCIÓN</p>
                            <p className="text-[10px]" style={{ color: pdfColors.text }}>Contenido del informe con el color de texto seleccionado.</p>
                            <p className="text-[9px] mt-1" style={{ color: pdfColors.footer }}>Pie de página · www.ejemplo.com</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-6 flex justify-end gap-4">
                {user.role === 'admin' && (
                    <button onClick={() => setShowClearModal(true)} className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-6 py-2 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 font-semibold border border-red-300 dark:border-red-800 transition-colors">
                        Zona de Peligro (Reset)
                    </button>
                )}
                <button onClick={handleSave} disabled={isSubmitting} className="bg-blue-600 text-white px-8 py-2 rounded-lg hover:bg-blue-700 font-bold shadow-lg transition-colors disabled:opacity-50">
                    {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
                </button>
            </div>
        </div>
    );
};

export default ParametersPage;